'use client';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

interface Category {
    id: number;
    name: string;
    slug: string;
}

interface Props {
    categories: Category[];
    merchantName: string;
    slug: string;
}

const MerchantCategorySlider: React.FC<Props> = ({ categories, merchantName, slug }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const syncState = useCallback(() => {
        const t = trackRef.current;
        if (!t) return;
        setCanPrev(t.scrollLeft > 4);
        setCanNext(t.scrollLeft < t.scrollWidth - t.clientWidth - 4);
    }, []);

    useEffect(() => {
        const t = trackRef.current;
        if (!t) return;
        syncState();
        t.addEventListener('scroll', syncState, { passive: true });
        window.addEventListener('resize', syncState);
        return () => {
            t.removeEventListener('scroll', syncState);
            window.removeEventListener('resize', syncState);
        };
    }, [categories, syncState]);

    const scroll = (dir: 'left' | 'right') => {
        trackRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
    };

    if (!categories?.length) return null;

    return (
        <>
            <div className="w-full h-[3px]"
                style={{ background: 'linear-gradient(90deg, transparent, #7AFC20 30%, #7AFC20 70%, transparent)' }} />

            <section className="relative w-full py-8 overflow-hidden" style={{ background: '#081C11' }}>
                {/* Dot grid */}
                <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #7AFC20 1px, transparent 0)', backgroundSize: '28px 28px' }} />
                {/* Ambient glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[200px] rounded-full blur-[120px] opacity-[0.05] pointer-events-none"
                    style={{ background: '#7AFC20' }} />

                <div className="container mx-auto px-4 md:px-8 relative z-10">

                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-px w-8 bg-gradient-to-r from-[#7AFC20] to-transparent" />
                        <div>
                            <p className="text-[9px] font-black uppercase tracking-[0.3em] m-0 mb-0.5"
                                style={{ color: 'rgba(122,252,32,0.5)' }}>
                                Explore Collections
                            </p>
                            <h2 className="text-xl md:text-2xl font-black m-0">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7AFC20] to-[#5cd010]">
                                    Product Categories{' '}
                                </span>
                                <span style={{ color: '#F5F5DC' }}>for {merchantName}</span>
                            </h2>
                        </div>
                    </div>

                    {/* Slider */}
                    <div className="relative">
                        {/* Prev */}
                        <button
                            onClick={() => scroll('left')}
                            disabled={!canPrev}
                            aria-label="Scroll left"
                            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-9 h-9 flex items-center justify-center transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none hover:scale-110 focus:outline-none"
                            style={{
                                background: 'rgba(8,28,17,0.7)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(122,252,32,0.4)',
                                clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                                boxShadow: '0 0 14px rgba(122,252,32,0.2)',
                            }}
                        >
                            <svg className="w-3.5 h-3.5" style={{ color: '#7AFC20' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        {/* Next */}
                        <button
                            onClick={() => scroll('right')}
                            disabled={!canNext}
                            aria-label="Scroll right"
                            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-9 h-9 flex items-center justify-center transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none hover:scale-110 focus:outline-none"
                            style={{
                                background: 'rgba(8,28,17,0.7)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(122,252,32,0.4)',
                                clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                                boxShadow: '0 0 14px rgba(122,252,32,0.2)',
                            }}
                        >
                            <svg className="w-3.5 h-3.5" style={{ color: '#7AFC20' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>

                        {/* Edge fades */}
                        <div className="absolute left-0 top-0 bottom-0 w-10 z-10 pointer-events-none transition-opacity duration-300"
                            style={{ background: 'linear-gradient(90deg, #081C11, transparent)', opacity: canPrev ? 1 : 0 }} />
                        <div className="absolute right-0 top-0 bottom-0 w-10 z-10 pointer-events-none transition-opacity duration-300"
                            style={{ background: 'linear-gradient(270deg, #081C11, transparent)', opacity: canNext ? 1 : 0 }} />

                        {/* Track */}
                        <div
                            ref={trackRef}
                            className="flex gap-3 overflow-x-auto py-2 px-1"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x mandatory' }}
                        >
                            <style>{`.mcs-track::-webkit-scrollbar{display:none}`}</style>

                            {categories.map((item, i) => (
                                <Link
                                    key={item.id || i}
                                    href={`/products/${slug}/${item.slug}`}
                                    className="group relative flex-shrink-0 flex items-center justify-center px-6 py-4 no-underline overflow-hidden transition-all duration-400 hover:-translate-y-1"
                                    style={{
                                        scrollSnapAlign: 'start',
                                        minWidth: '180px',
                                        background: 'rgba(245,245,220,0.04)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(245,245,220,0.08)',
                                        clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                                    }}
                                >
                                    {/* Hover fill */}
                                    <div
                                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
                                        style={{
                                            background: 'rgba(122,252,32,0.08)',
                                            boxShadow: 'inset 0 0 0 1px rgba(122,252,32,0.4)',
                                            clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                                        }}
                                    />

                                    <span
                                        className="relative z-10 text-[11px] font-black uppercase tracking-[0.2em] text-center transition-colors duration-300 group-hover:text-[#7AFC20]"
                                        style={{ color: 'rgba(245,245,220,0.6)' }}
                                    >
                                        {item.name}
                                    </span>

                                    {/* Bottom accent */}
                                    <div
                                        className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                                        style={{ background: 'linear-gradient(90deg, transparent, #7AFC20, transparent)' }}
                                    />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-[3px]"
                    style={{ background: 'linear-gradient(90deg, transparent, #7AFC20 30%, #7AFC20 70%, transparent)' }} />
            </section>
        </>
    );
};

export default MerchantCategorySlider;
